import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { isSupabaseConfigured } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import { FiBarChart2, FiDatabase, FiHome, FiMail, FiMic, FiPhone, FiSend, FiSettings, FiUsers, FiWifi, FiWifiOff, FiX } from 'react-icons/fi';


const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const dbConnected = isSupabaseConfigured();

  const menuItems = [
    { path: '/', icon: FiHome, label: 'Dashboard' },
    { path: '/contacts', icon: FiUsers, label: 'Contacts' },
    { path: '/campaigns', icon: FiMail, label: 'Campaigns' },
    { path: '/email', icon: FiSend, label: 'Email Marketing' },
    { path: '/agents', icon: FiMic, label: 'AI Agents' },
    { path: '/analytics', icon: FiBarChart2, label: 'Analytics' },
    { path: '/settings', icon: FiSettings, label: 'Settings' }
  ];

  return (
    <>
      {/* Mobile overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <div
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white shadow-lg lg:shadow-none transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiPhone} className="text-white text-lg" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Prosper</h1>
                <p className="text-xs text-gray-500">CRM</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="lg:hidden p-1 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <SafeIcon icon={FiX} className="text-gray-500" />
            </button>
          </div>

          {/* Database Status */}
          <div className="p-4 border-b border-gray-200">
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                dbConnected ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'
              }`}
            >
              <SafeIcon icon={dbConnected ? FiWifi : FiWifiOff} className="text-sm" />
              <span className="text-xs font-medium">
                {dbConnected ? 'Database Connected' : 'Using Mock Data'}
              </span>
            </div>
            {!dbConnected && (
              <p className="text-xs text-gray-500 mt-2 px-3">
                Connect Supabase in Settings to enable data persistence
              </p>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <SafeIcon icon={item.icon} className={`text-lg ${isActive ? 'text-blue-700' : 'text-gray-400'}`} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 text-sm">Need Help?</h3>
              <p className="text-xs text-gray-600 mt-1">
                Check our documentation for guides and tutorials.
              </p>
              <button className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium">
                View Docs →
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;